import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaVideo } from 'react-icons/fa';
import { useSettings } from '../../context/SettingsContext';
import AdminEditableSection from '../admin/AdminEditableSection';
import DeferredPageText from '../common/DeferredPageText';
import VideoGallery from '../gallery/VideoGallery';
import Lightbox from '../gallery/Lightbox';
import useMediaPlayback from '../../hooks/useMediaPlayback';
import useLightbox from '../../hooks/useLightbox';
import initScrollAnimations from '../../utils/scrollAnimation';

const FeaturedVideos = () => {
  const { pageContent } = useSettings();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  const {
    isPlaying,
    currentTimes,
    durations,
    isMuted,
    playbackErrors,
    videoRefs,
    progressRefs,
    toggleVideo,
    toggleMute,
    handleTimeUpdate,
    handleMediaEnded,
    handleMetadataLoaded,
    handleSeek,
    clearPlaybackError,
    resetVideoState
  } = useMediaPlayback();

  const {
    lightboxOpen,
    currentMedia,
    openLightbox,
    closeLightbox,
    navigateLightbox,
    handleTouchStart,
    handleTouchEnd,
    handleLightboxImageLoad,
    handleLightboxImageError,
    retryLightbox
  } = useLightbox();

  useEffect(() => {
    let cancelled = false;

    const fetchFeaturedVideos = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/media`, {
          params: {
            featured: true,
            mediaType: 'video',
            limit: 3
          }
        });

        if (!cancelled) {
          setVideos(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error('Error fetching featured videos:', err);
        if (!cancelled) {
          setVideos([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFeaturedVideos();

    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    if (!loading && videos.length > 0) {
      initScrollAnimations();
    }
  }, [loading, videos]);

  if (loading || videos.length === 0) {
    return null;
  }

  return (
    <>
      <section className="section featured-videos-section">
        <div className="container">
          <AdminEditableSection
            title="Edit featured video section"
            fields={[
              {
                name: 'featuredVideoTitle',
                label: 'Section title',
                path: 'home.featuredVideoTitle',
                type: 'text'
              },
              {
                name: 'featuredVideoSubtitle',
                label: 'Section subtitle',
                path: 'home.featuredVideoSubtitle',
                type: 'textarea'
              }
            ]}
          >
            <h2 className="section-title animate-on-scroll" style={{ textAlign: 'center' }}>
              <DeferredPageText>{pageContent.home.featuredVideoTitle}</DeferredPageText>
            </h2>
          </AdminEditableSection>

          <p className="featured-videos-subtitle animate-on-scroll">
            <DeferredPageText skeletonClassName="text-skeleton text-skeleton--paragraph">
              {pageContent.home.featuredVideoSubtitle}
            </DeferredPageText>
          </p>

          <div className={`featured-videos-player featured-videos-player--${videos.length} animate-on-scroll`}>
            <VideoGallery
              videoItems={videos}
              visibleCount={videos.length}
              loading={false}
              isPlaying={isPlaying}
              currentTimes={currentTimes}
              durations={durations}
              isMuted={isMuted}
              playbackErrors={playbackErrors}
              videoRefs={videoRefs}
              progressRefs={progressRefs}
              toggleVideo={toggleVideo}
              toggleMute={toggleMute}
              handleTimeUpdate={handleTimeUpdate}
              handleMediaEnded={handleMediaEnded}
              handleMetadataLoaded={handleMetadataLoaded}
              handleSeek={handleSeek}
              clearPlaybackError={clearPlaybackError}
              openLightbox={openLightbox}
            />
          </div>

          <div className="featured-videos-cta animate-on-scroll">
            <Link to="/gallery" className="btn btn-secondary">
              <FaVideo /> View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      <Lightbox
        lightboxOpen={lightboxOpen}
        currentMedia={currentMedia}
        closeLightbox={closeLightbox}
        navigateLightbox={navigateLightbox}
        handleTouchStart={handleTouchStart}
        handleTouchEnd={handleTouchEnd}
        handleLightboxImageLoad={handleLightboxImageLoad}
        handleLightboxImageError={handleLightboxImageError}
        retryLightbox={retryLightbox}
        isPlaying={isPlaying}
        currentTimes={currentTimes}
        durations={durations}
        isMuted={isMuted}
        videoRefs={videoRefs}
        progressRefs={progressRefs}
        toggleVideo={toggleVideo}
        toggleMute={toggleMute}
        handleTimeUpdate={handleTimeUpdate}
        handleMetadataLoaded={handleMetadataLoaded}
        handleSeek={handleSeek}
        resetVideoState={resetVideoState}
      />
    </>
  );
};

export default FeaturedVideos;
